/**
 * Settings Store — Zustand (persisted)
 *
 * Holds user-facing preferences that survive page reloads via localStorage.
 * Separated from `useAuthStore` (which handles XSS-sensitive token + hydration
 * state) and from `useGameStore` / `useMatchmakingStore` (ephemeral game state).
 *
 * When the `soundEnabled` value changes, the store imperatively pushes the new
 * value into `audioController.setSoundEnabled()` so the audio engine stays
 * synchronised with the UI toggle regardless of which codepath changed it.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { audioController } from '../utils/audioController';

export interface SettingsState {
  /** Whether chess sounds are emitted. Defaults true. */
  soundEnabled: boolean;

  /** Direct setter — also pushes into the audio controller. */
  setSoundEnabled: (enabled: boolean) => void;

  /** Flip the current value (toggle). */
  toggleSound: () => void;
}

const initialSoundEnabled =
  typeof localStorage !== 'undefined'
    ? ((): boolean => {
        try {
          const raw = localStorage.getItem('fog-chess-settings');
          if (!raw) return true;
          const parsed = JSON.parse(raw);
          return parsed?.state?.soundEnabled ?? true;
        } catch {
          return true;
        }
      })()
    : true;

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      soundEnabled: initialSoundEnabled,

      setSoundEnabled: (enabled: boolean): void => {
        set({ soundEnabled: enabled });
        audioController.setSoundEnabled(enabled);
      },

      toggleSound: (): void => {
        const next = !get().soundEnabled;
        set({ soundEnabled: next });
        audioController.setSoundEnabled(next);
      },
    }),
    {
      name: 'fog-chess-settings',
      storage: typeof window !== 'undefined'
        ? undefined // default (localStorage)
        : undefined,
      // Only persist soundEnabled; keep the shape uniform for future settings.
      partialize: (state) => ({ soundEnabled: state.soundEnabled }),
      skipHydration: typeof window === 'undefined',
    },
  ),
);

/**
 * One-shot: push the persisted value into the audio controller on store creation
 * so that a page that was muted in a previous session stays muted from the very
 * first socket event, before any React component has mounted.
 */
audioController.setSoundEnabled(useSettingsStore.getState().soundEnabled);

// ---------------------------------------------------------------------------
// Selectors for granular React subscriptions
// ---------------------------------------------------------------------------

export const useSoundEnabled = (): boolean =>
  useSettingsStore((s) => s.soundEnabled);

export const useSettings = (): {
  soundEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
  toggleSound: () => void;
} => {
  const soundEnabled = useSettingsStore((s) => s.soundEnabled);
  const setSoundEnabled = useSettingsStore((s) => s.setSoundEnabled);
  const toggleSound = useSettingsStore((s) => s.toggleSound);
  return { soundEnabled, setSoundEnabled, toggleSound };
};